import React, { useEffect, useState, memo, useRef, useMemo } from 'react';
import { View, Text } from '@adobe/react-spectrum';
import { FormField } from '../../store/slice';
import { fetchDropdownOptions } from '../../store/api';
import { getCookie } from '../../../../../utils/helpers';
import styles from './MultiSelectApiDropdown.module.scss';
import { fetchEventsCached } from '../../../../services/momentsApi';
import { productHierarchyService } from '../../services/productHierarchyService';
import ChevronDown from '@spectrum-icons/workflow/ChevronDown';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import Close from '@spectrum-icons/workflow/Close';
import Alert from '@spectrum-icons/workflow/Alert';

interface OptionItem {
  value: string;
  label: string;
  group?: string; // For grouped options
}

interface MultiSelectApiDropdownProps {
  field: FormField;
  value: string[];
  onChange: (name: string, value: string[]) => void;
  formValues: Record<string, any>;
  onFocus?: () => void;
  isInvalid?: boolean;
  disabled?: boolean;
}

// Cache for dropdown options to prevent unnecessary API calls
const dropdownOptionsCache: Record<string, OptionItem[]> = {};

/**
 * Component specifically for rendering multi-select API-based dropdown fields
 */
export const MultiSelectApiDropdown: React.FC<MultiSelectApiDropdownProps> =
  memo(
    ({
      field,
      value = [],
      onChange,
      formValues,
      onFocus,
      isInvalid,
      disabled = false
    }) => {
      const [options, setOptions] = useState<OptionItem[]>([]);
      // Ref to always have access to latest options (for grouped fields)
      const optionsRef = useRef<OptionItem[]>([]);
      // For Audience: persistent full list from initial call
      const allAudiencesRef = useRef<OptionItem[] | null>(null);
      const [isLoading, setIsLoading] = useState<boolean>(false);
      const [error, setError] = useState<string | null>(null);
      const [isOpen, setIsOpen] = useState(false);
      const dropdownRef = useRef<HTMLDivElement>(null);
      const prevDependencyValuesRef = useRef<string>('');
      const prevOptionsCountRef = useRef<number>(0);
      const optionsListRef = useRef<HTMLDivElement>(null);
      const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
      const [query, setQuery] = useState<string>('');
      const searchInputRef = useRef<HTMLInputElement>(null);
      // For grouped options (e.g., strategic alignment)
      const [groupedOptions, setGroupedOptions] = useState<
        Record<string, OptionItem[]>
      >({});

      const displayedOptions = useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase();
        return options.filter(opt => opt.label.toLowerCase().includes(q));
      }, [options, query]);

      const toggleOpen = () => {
        if (!isLoading && !disabled) {
          setIsOpen(!isOpen);
          if (onFocus) onFocus();
        }
      };

      const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        // ✅ Auto-focus search input when typing alphanumeric characters
        if (isOpen && !e.ctrlKey && !e.metaKey && !e.altKey) {
          // Check if it's an alphanumeric key (letter, number, or space)
          const isAlphanumeric = /^[a-zA-Z0-9 ]$/.test(e.key);

          if (isAlphanumeric) {
            // Focus the search input and let the character be typed
            if (
              searchInputRef.current &&
              document.activeElement !== searchInputRef.current
            ) {
              searchInputRef.current.focus();
              // The character will be captured by the input's onChange
              return;
            }
          }
        }

        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (!isOpen) {
            toggleOpen();
            setHighlightedIndex(prev => (prev >= 0 ? prev : 0));
          } else if (
            highlightedIndex >= 0 &&
            highlightedIndex < displayedOptions.length
          ) {
            const opt = displayedOptions[highlightedIndex];
            toggleOption(opt.value);
          }
        } else if (e.key === 'ArrowDown' && !isOpen) {
          e.preventDefault();
          toggleOpen();
          setHighlightedIndex(0);
        } else if (e.key === 'ArrowDown' && isOpen) {
          e.preventDefault();
          setHighlightedIndex(prev => {
            const next = Math.min(
              (prev < 0 ? -1 : prev) + 1,
              displayedOptions.length - 1
            );
            // scroll
            requestAnimationFrame(() => {
              const el = optionsListRef.current?.querySelector(
                `[data-option-index="${next}"]`
              ) as HTMLElement | null;
              el?.scrollIntoView({ block: 'nearest' });
            });
            return next;
          });
        } else if (e.key === 'ArrowUp' && isOpen) {
          e.preventDefault();
          setHighlightedIndex(prev => {
            const next = Math.max((prev < 0 ? 0 : prev) - 1, 0);
            requestAnimationFrame(() => {
              const el = optionsListRef.current?.querySelector(
                `[data-option-index="${next}"]`
              ) as HTMLElement | null;
              el?.scrollIntoView({ block: 'nearest' });
            });
            return next;
          });
        } else if (e.key === 'Escape' && isOpen) {
          e.preventDefault();
          setIsOpen(false);
        }
      };

      useEffect(() => {
        if (!isOpen) {
          setHighlightedIndex(-1);
          setQuery('');
        } else if (displayedOptions.length > 0 && highlightedIndex === -1) {
          // default to first option when opening
          setHighlightedIndex(0);
        }

        // ✅ Auto-focus search input when dropdown opens
        if (isOpen && searchInputRef.current) {
          // Small delay to ensure the dropdown is rendered
          setTimeout(() => {
            searchInputRef.current?.focus();
          }, 50);
        }
      }, [isOpen, displayedOptions.length]);

      // Close dropdown when clicking outside
      useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
          const target = event.target as Node;
          if (
            dropdownRef.current &&
            !dropdownRef.current.contains(target) &&
            optionsListRef.current &&
            !optionsListRef.current.contains(target)
          ) {
            setIsOpen(false);
          }
        };

        if (isOpen) {
          document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }, [isOpen]);

      // Fetch options from API
      useEffect(() => {
        const fetchOptionsFromApi = async () => {
          if (!field.api) return;

          // Skip cache for certain fields
          const skipCache =
            field.name === 'categories' ||
            field.name === 'products' ||
            field.name === 'customerAudiences' ||
            field.name === 'productGroups' ||
            field.name === 'audienceStrategyMappings' ||
            field.api === 'moments/type/event';

          if (!skipCache && dropdownOptionsCache[field.api]) {
            const cachedOptions = dropdownOptionsCache[field.api];
            setOptions(cachedOptions);
            optionsRef.current = cachedOptions; // Update ref
            return;
          }

          // Check dependencies and create dependency values string for comparison
          let dependencyValues = '';
          if (field.dependsOn && formValues) {
            const dependencies = Array.isArray(field.dependsOn)
              ? field.dependsOn
              : [field.dependsOn];

            const isEmpty = (v: any) =>
              v === undefined ||
              v === null ||
              (Array.isArray(v) && v.length === 0) ||
              (typeof v === 'string' && v.trim() === '');

            const missingDependency = dependencies.some(dep =>
              isEmpty(formValues[dep])
            );
            if (missingDependency) {
              setOptions([]);
              optionsRef.current = []; // Update ref
              // Clear selection if dependency is missing and we have a value
              if (Array.isArray(value) && value.length > 0) {
                onChange(field.name, []);
              }
              return;
            }

            // Create a string of dependency values for comparison
            dependencyValues = dependencies
              .map(dep => `${dep}:${formValues[dep]}`)
              .join('|');
          }

          // Skip if dependencies haven't changed and we already have options
          if (
            field.dependsOn &&
            dependencyValues === prevDependencyValuesRef.current &&
            options.length > 0
          ) {
            return;
          }

          prevDependencyValuesRef.current = dependencyValues;

          setIsLoading(true);
          setError(null);

          try {
            // Special handling for product-hierarchy-filters endpoint
            if (field.api === 'product-hierarchy-filters') {
              try {
                let mappedOptions: OptionItem[] = [];

                if (field.name === 'products') {
                  // For initial product list
                  const products =
                    await productHierarchyService.fetchProducts();
                  mappedOptions = products.map((item: string) => ({
                    value: item,
                    label: item
                  }));
                } else if (
                  field.name === 'customerAudiences' &&
                  !formValues.products
                ) {
                  // Audience field with no product selected: show full list (initial call)
                  if (!allAudiencesRef.current) {
                    const allAudiences =
                      await productHierarchyService.fetchAllAudiences();
                    allAudiencesRef.current = allAudiences.map(
                      (item: string) => ({ value: item, label: item })
                    );
                  }
                  mappedOptions = allAudiencesRef.current || [];
                  // Auto-select all initially
                  const allValuesInit = mappedOptions.map(opt => opt.value);
                  const currentSetInit = new Set(value);
                  const alreadyAllInit =
                    value.length === allValuesInit.length &&
                    allValuesInit.every(v => currentSetInit.has(v));
                  if (!alreadyAllInit) {
                    onChange(field.name, allValuesInit);
                  }
                } else if (
                  field.dependsOn === 'products' &&
                  formValues.products
                ) {
                  // For dependent fields (categories, productGroups, customerAudiences)
                  const dependentData =
                    await productHierarchyService.fetchDependentOptions(
                      formValues.products
                    );

                  if (field.name === 'categories') {
                    mappedOptions = dependentData.categories.map(
                      (item: string) => ({
                        value: item,
                        label: item
                      })
                    );
                  } else if (field.name === 'productGroups') {
                    mappedOptions = dependentData.productGroups.map(
                      (item: string) => ({
                        value: item,
                        label: item
                      })
                    );
                  } else if (field.name === 'customerAudiences') {
                    const subset = dependentData.customerAudiences.map(
                      (item: string) => ({ value: item, label: item })
                    );
                    // Ensure full list is available in options, subset auto-selected
                    if (!allAudiencesRef.current) {
                      const allAudiences =
                        await productHierarchyService.fetchAllAudiences();
                      allAudiencesRef.current = allAudiences.map(
                        (item: string) => ({ value: item, label: item })
                      );
                    }
                    // Merge: keep stable full list for display
                    mappedOptions = allAudiencesRef.current || [];
                    // Auto-select all from subset
                    const allValues = subset.map(opt => opt.value);
                    const currentSet = new Set(value);
                    const alreadyAllSelected = allValues.every(v =>
                      currentSet.has(v)
                    );
                    if (!alreadyAllSelected) {
                      onChange(
                        field.name,
                        Array.from(new Set([...value, ...allValues]))
                      );
                    }
                  }
                } else if (
                  field.name === 'audienceStrategyMappings' &&
                  field.dependsOn === 'customerAudiences' &&
                  formValues.customerAudiences &&
                  formValues.products
                ) {
                  // Handle grouped audience strategy mappings field
                  // Get selected audiences first
                  const selectedAudiences = Array.isArray(
                    formValues.customerAudiences
                  )
                    ? formValues.customerAudiences
                    : [];

                  // Fetch dependent data based on products AND customerAudiences
                  // This will filter audienceStrategyMappings to only show options for selected audiences
                  const dependentData =
                    await productHierarchyService.fetchDependentOptions(
                      formValues.products,
                      selectedAudiences
                    );

                  const audienceStrategyMappingsData =
                    dependentData.audienceStrategyMappings || {};

                  // Build grouped options and flatten for main options array
                  const grouped: Record<string, OptionItem[]> = {};
                  const flattened: OptionItem[] = [];

                  Object.entries(audienceStrategyMappingsData).forEach(
                    ([category, items]) => {
                      const categoryOptions = items.map((item: string) => ({
                        value: item,
                        label: item,
                        group: category
                      }));

                      grouped[category] = categoryOptions;
                      flattened.push(...categoryOptions);
                    }
                  );

                  setGroupedOptions(grouped);
                  mappedOptions = flattened;

                  // Store category mapping for payload builder
                  // This allows the payload to convert array to grouped structure
                  const categoryMap: Record<string, string> = {};
                  flattened.forEach(item => {
                    if (item.group) {
                      categoryMap[item.value] = item.group;
                    }
                  });
                  // Store in hidden fields that the payload builder can use
                  if (formValues) {
                    formValues[`${field.name}_categoryMap`] = categoryMap;
                    // Store all available categories to ensure empty categories appear in payload
                    formValues[`${field.name}_categories`] =
                      Object.keys(grouped);
                  }
                }

                setOptions(mappedOptions);
                optionsRef.current = mappedOptions; // Update ref for immediate access
                if (!skipCache) {
                  dropdownOptionsCache[field.api] = mappedOptions;
                }

                // ✅ Clean up invalid selections for audienceStrategyMappings
                if (
                  field.name === 'audienceStrategyMappings' &&
                  Array.isArray(value) &&
                  value.length > 0
                ) {
                  const validValues = value.filter(v =>
                    mappedOptions.some(opt => opt.value === v)
                  );

                  // Update value if any invalid selections were removed
                  if (validValues.length !== value.length) {
                    onChange(field.name, validValues);
                  }
                }

                // Auto-select all for categories and productGroups
                if (
                  field.name === 'categories' ||
                  field.name === 'productGroups'
                ) {
                  const allValues = mappedOptions.map(opt => opt.value);
                  const currentSet = new Set(value);
                  const alreadyAllSelected =
                    value.length === allValues.length &&
                    allValues.every(v => currentSet.has(v));
                  if (!alreadyAllSelected) {
                    onChange(field.name, allValues);
                  }
                } else if (field.name !== 'customerAudiences') {
                  // Default: auto-select when only one option; clear when moving from 1 -> many
                  if (mappedOptions.length === 1) {
                    onChange(field.name, [mappedOptions[0].value]);
                  } else if (
                    value.length > 0 &&
                    prevOptionsCountRef.current === 1
                  ) {
                    onChange(field.name, []);
                  }
                }
                // Update the options count reference
                prevOptionsCountRef.current = mappedOptions.length;

                return;
              } catch (error) {
                setError(
                  `Failed to load options: ${
                    error instanceof Error ? error.message : 'Unknown error'
                  }`
                );
                return;
              }
            }

            // Special handling for events endpoint - use centralized service to avoid duplicate API calls
            if (field.api === 'moments/type/event') {
              console.log(
                '🎪 MultiSelectApiDropdown: Using centralized events service to avoid duplicate calls'
              );
              const response = await fetchEventsCached();

              if (response.success && response.data) {
                const formattedOptions = response.data.map((event: any) => ({
                  value: event.id,
                  label: event.name
                }));
                setOptions(formattedOptions);
                console.log(
                  `✅ MultiSelectApiDropdown: Loaded ${response.data.length} events from centralized cache`
                );
              } else {
                console.error(
                  '❌ MultiSelectApiDropdown: Failed to load events:',
                  response.error
                );
                setError(response.message || 'Failed to load events');
                setOptions([]);
              }
              setIsLoading(false);
              return;
            }

            // Default handling for other APIs
            let url = field.api;

            // If the URL is not absolute, prefix with the v2 API base URL
            if (!url.startsWith('http')) {
              const baseUrl = process.env.REACT_APP_API_V2_URL;
              if (!baseUrl) {
                throw new Error('API base URL is not configured');
              }
              url = `${baseUrl.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
            }

            // Get auth token
            const token = getCookie('app_access_token');
            const headers: HeadersInit = {
              'Content-Type': 'application/json'
            };

            if (token) {
              headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(url, {
              method: 'GET',
              headers
            });

            if (!response.ok) {
              throw new Error(`API responded with status: ${response.status}`);
            }

            const data = await response.json();

            // Handle different response formats
            let items: any[] = [];

            if (Array.isArray(data)) {
              items = data;
            } else if (data && typeof data === 'object') {
              const arrayProps = Object.keys(data).filter(key =>
                Array.isArray(data[key])
              );
              if (arrayProps.length > 0) {
                const arrayProp = arrayProps.includes('items')
                  ? 'items'
                  : arrayProps[0];
                items = data[arrayProp];
              }
            }

            const mappedOptions = items.map((item: any) => ({
              value: item.id || item.value || '',
              label: item.name || item.label || ''
            }));

            // Cache the options if needed
            if (!skipCache) {
              dropdownOptionsCache[field.api] = mappedOptions;
            }

            setOptions(mappedOptions);
            optionsRef.current = mappedOptions; // Update ref for immediate access

            // Special auto-select behavior for Audience: select ALL options
            if (field.name === 'customerAudiences') {
              const allValues = mappedOptions.map((opt: any) => opt.value);
              const currentSet = new Set(value);
              const alreadyAllSelected =
                value.length === allValues.length &&
                allValues.every(v => currentSet.has(v));
              if (!alreadyAllSelected) {
                onChange(field.name, allValues);
              }
            } else if (
              field.name === 'categories' ||
              field.name === 'productGroups'
            ) {
              // Auto-select all options for categories/productGroups
              const allValues = mappedOptions.map((opt: any) => opt.value);
              const currentSet = new Set(value);
              const alreadyAllSelected =
                value.length === allValues.length &&
                allValues.every((v: any) => currentSet.has(v));
              if (!alreadyAllSelected) {
                onChange(field.name, allValues);
              }
            } else {
              // Default: auto-select when only one option; clear when moving from 1 -> many
              if (mappedOptions.length === 1) {
                onChange(field.name, [mappedOptions[0].value]);
              } else if (
                value.length > 0 &&
                prevOptionsCountRef.current === 1
              ) {
                onChange(field.name, []);
              }
            }
            // Update the options count reference
            prevOptionsCountRef.current = mappedOptions.length;
          } catch (error) {
            setError(
              `Failed to load options: ${
                error instanceof Error ? error.message : 'Unknown error'
              }`
            );
          } finally {
            setIsLoading(false);
          }
        };

        fetchOptionsFromApi();
      }, [field.api, field.dependsOn, field.name, formValues]);

      // Toggle option selection
      const toggleOption = (optionValue: string) => {
        const newValue = [...value];
        const index = newValue.indexOf(optionValue);

        if (index === -1) {
          newValue.push(optionValue);
        } else {
          newValue.splice(index, 1);
        }

        onChange(field.name, newValue);
      };

      // Get display text for selected options
      const getSelectedText = () => {
        if (value.length === 0) {
          return 'Select options';
        }
        return `${value.length} selected`;
      };

      // Remove a selected option
      const handleRemoveOption = (optionValue: string, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent dropdown from opening
        const newValue = value.filter(v => v !== optionValue);
        onChange(field.name, newValue);
      };

      const optionsListContent = (
        <>
          <div className={styles.searchBar} onClick={e => e.stopPropagation()}>
            <svg
              className={styles.searchIcon}
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M14.824 13.9766L10.7574 9.90996C11.5329 8.94882 11.9998 7.72881 11.9998 6.40078C11.9998 3.31328 9.48731 0.800781 6.3998 0.800781C3.3123 0.800781 0.799805 3.31327 0.799805 6.40077C0.799805 9.48828 3.3123 12.0008 6.3998 12.0008C7.72783 12.0008 8.94785 11.5339 9.90898 10.7584L13.9756 14.825C14.0928 14.9422 14.2459 15.0008 14.3998 15.0008C14.5537 15.0008 14.7068 14.9422 14.824 14.825C15.0584 14.5906 15.0584 14.2109 14.824 13.9766ZM6.3998 10.8008C3.97402 10.8008 1.9998 8.82656 1.9998 6.40077C1.9998 3.97499 3.97402 2.00077 6.3998 2.00077C8.82559 2.00077 10.7998 3.97499 10.7998 6.40077C10.7998 8.82656 8.82559 10.8008 6.3998 10.8008Z"
                fill="#222222"
              />
            </svg>
            <input
              ref={searchInputRef}
              className={styles.searchInput}
              type="text"
              role="searchbox"
              placeholder="Search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setHighlightedIndex(prev =>
                    Math.min(
                      (prev < 0 ? -1 : prev) + 1,
                      Math.max(0, displayedOptions.length - 1)
                    )
                  );
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHighlightedIndex(prev =>
                    Math.max((prev < 0 ? 0 : prev) - 1, 0)
                  );
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  if (
                    highlightedIndex >= 0 &&
                    highlightedIndex < displayedOptions.length
                  ) {
                    const opt = displayedOptions[highlightedIndex];
                    toggleOption(opt.value);
                  }
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setIsOpen(false);
                }
              }}
            />
          </div>

          {field.grouped && Object.keys(groupedOptions).length > 0
            ? // GROUPED RENDERING (e.g., strategic alignment)
              (() => {
                let visibleCategoryIndex = 0; // Track first visible category
                return Object.entries(groupedOptions).map(
                  ([category, categoryItems], categoryIndex) => {
                    const filteredItems = query
                      ? categoryItems.filter(opt =>
                          opt.label.toLowerCase().includes(query.toLowerCase())
                        )
                      : categoryItems;

                    if (filteredItems.length === 0) return null;

                    const isFirstVisible = visibleCategoryIndex === 0;
                    visibleCategoryIndex++; // Increment for next visible category

                    return (
                      <div
                        key={category}
                        className={`${styles.categorySection} ${
                          isFirstVisible ? styles.firstCategory : ''
                        }`}
                      >
                        <div className={styles.categoryHeader}>{category}</div>
                        {filteredItems.map(option => (
                          <div
                            key={option.value}
                            className={`${styles.option} ${
                              styles.groupedOption
                            } ${
                              value.includes(option.value)
                                ? styles.selected
                                : ''
                            }`}
                            onClick={e => {
                              e.stopPropagation();
                              toggleOption(option.value);
                            }}
                            role="option"
                            aria-selected={value.includes(option.value)}
                          >
                            <span>{option.label}</span>
                            {value.includes(option.value) && (
                              <BlackCheckMarkIcon
                                className={styles.checkmark}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  }
                );
              })()
            : // FLAT RENDERING (existing fields)
              displayedOptions.map((option, idx) => (
                <div
                  key={option.value}
                  className={`${styles.option} ${
                    value.includes(option.value) ? styles.selected : ''
                  }`}
                  onClick={e => {
                    e.stopPropagation();
                    toggleOption(option.value);
                  }}
                  role="option"
                  aria-selected={value.includes(option.value)}
                  id={`msapi-opt-${field.name}-${idx}`}
                  data-option-index={idx}
                >
                  <span>{option.label}</span>
                  {value.includes(option.value) && (
                    <BlackCheckMarkIcon className={styles.checkmark} />
                  )}
                </div>
              ))}

          {displayedOptions.length === 0 && !isLoading && (
            <div className={styles.noOptions}>
              {options.length === 0 ? 'No options available' : 'No results'}
            </div>
          )}
        </>
      );

      return (
        <>
          <div className={styles.fieldWrapper}>
            <label className={styles.fieldLabel}>
              {field.label}
              {field.required && <span className={styles.required}> *</span>}
              {field.subtitle && (
                <span className={styles.fieldSubtitle}>{field.subtitle}</span>
              )}
            </label>
            <div
              ref={dropdownRef}
              className={`${styles.dropdown} ${isOpen ? styles.open : ''} ${
                isInvalid ? styles.invalid : ''
              } ${disabled ? styles.disabled : ''}`}
              onClick={() => {
                toggleOpen();
              }}
              role="combobox"
              aria-expanded={isOpen}
              aria-haspopup="listbox"
              aria-disabled={isLoading || disabled}
              tabIndex={disabled ? -1 : 0}
              onKeyDown={handleKeyDown}
            >
              <div className={styles.selectedText}>{getSelectedText()}</div>
              {isInvalid && <Alert UNSAFE_className={styles.errorIcon} />}
              <ChevronDown
                UNSAFE_className={`${styles.chevron} ${
                  isOpen ? styles.open : ''
                }`}
              />

              {isOpen && (
                <div
                  ref={optionsListRef}
                  className={`${styles.optionsList} ${
                    field.grouped ? styles.groupedDropdown : ''
                  }`}
                  role="listbox"
                  aria-multiselectable="true"
                >
                  {optionsListContent}
                </div>
              )}
            </div>

            {/* Selected tags - Hide when field is grouped */}
            {value.length > 0 && !field.grouped && (
              <div className={styles.tagContainer}>
                {value.map((v: string) => {
                  const option = options.find(opt => opt.value === v);
                  if (!option) return null;
                  return (
                    <div key={v} className={styles.selectedTag}>
                      <span>{option.label}</span>
                      <button
                        className={styles.tagRemoveButton}
                        onClick={e => handleRemoveOption(v, e)}
                        aria-label={`Remove ${option.label}`}
                      >
                        <span
                          className={styles['safari-icon-fix']}
                          style={
                            {
                              '--spectrum-icon-size-s': '11px'
                            } as React.CSSProperties
                          }
                        >
                          <Close size="S" />
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {isLoading && <Text>Loading options...</Text>}
          {error && <Text UNSAFE_className={styles.error}>{error}</Text>}
        </>
      );
    },
    (prevProps, nextProps) => {
      if (prevProps.value !== nextProps.value) return false;
      if (prevProps.isInvalid !== nextProps.isInvalid) return false;
      if (prevProps.field !== nextProps.field) return false;
      // Shallow compare dependency-related formValues to re-render when upstream changes
      const prevDepends = prevProps.field?.dependsOn;
      const nextDepends = nextProps.field?.dependsOn;
      const toArray = (d: any) => (Array.isArray(d) ? d : d ? [d] : []);
      const prevDeps = toArray(prevDepends);
      const nextDeps = toArray(nextDepends);
      if (prevDeps.length !== nextDeps.length) return false;
      for (let i = 0; i < prevDeps.length; i++) {
        const dep = prevDeps[i];
        if (prevProps.formValues?.[dep] !== nextProps.formValues?.[dep])
          return false;
      }
      return true;
    }
  );
